/**
 * PublishButton - Publish / Update / Unpublish workspace snapshots (RFC-0295).
 *
 * Renders a button in the workspace toolbar. On click, opens a popover
 * showing publication status with Publish / Update / Unpublish actions.
 * Each publish creates a version with its own unique share URL.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { Share2, Copy, Check, Loader2, RotateCw, EyeOff } from 'lucide-react'
import { usePublicationStatus, usePublishProject, useUnpublishProject } from '../../api/hooks'
import type { PublicationVersionInfo } from '../../api/client'

interface PublishButtonProps {
  projectId: string
}

export function PublishButton({ projectId }: PublishButtonProps) {
  const [open, setOpen] = useState(false)
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const { data: status, isLoading: statusLoading } = usePublicationStatus(projectId)
  const publishMutation = usePublishProject()
  const unpublishMutation = useUnpublishProject()

  // Close popover on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handlePublish = useCallback(async () => {
    try {
      await publishMutation.mutateAsync(projectId)
    } catch (err) {
      console.error('Failed to publish project:', err)
    }
  }, [projectId, publishMutation])

  const handleUnpublish = useCallback(async () => {
    try {
      await unpublishMutation.mutateAsync(projectId)
    } catch (err) {
      console.error('Failed to unpublish project:', err)
    }
  }, [projectId, unpublishMutation])

  const handleCopyVersion = useCallback((version: PublicationVersionInfo) => {
    const fullUrl = `${window.location.origin}${version.share_url}`
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopiedSlug(version.version_slug)
      setTimeout(() => setCopiedSlug(null), 2000)
    })
  }, [])

  const isPublishing = publishMutation.isPending
  const isUnpublishing = unpublishMutation.isPending
  const isBusy = isPublishing || isUnpublishing

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={() => setOpen(!open)}
        className="p-2 rounded-lg bg-surface-light/80 backdrop-blur-sm border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all text-muted-foreground hover:text-primary cursor-pointer"
        title="Publish project"
        data-testid="publish-button"
      >
        <Share2 className="w-4 h-4" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden"
          data-testid="publish-popover"
        >
          <div className="p-3 border-b border-border">
            <h3 className="text-sm font-medium text-white">Publish Project</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Publish a read-only snapshot of your canvas for anyone with the link.
            </p>
          </div>

          <div className="p-3">
            {statusLoading ? (
              <div className="flex justify-center py-2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            ) : status?.is_published ? (
              /* Published state */
              <div className="space-y-3">
                {/* Version history */}
                {status.versions.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">Published versions</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {status.versions.map((v) => (
                        <div
                          key={v.version_slug}
                          className="flex items-center gap-2 px-2 py-1.5 rounded bg-surface-light/50"
                          data-testid={`version-row-${v.version_number}`}
                        >
                          <span className="text-xs text-white/70 flex-1 truncate">
                            v{v.version_number}
                            <span className="text-white/40 ml-1.5">
                              {new Date(v.created_at).toLocaleDateString()}
                            </span>
                          </span>
                          <button
                            onClick={() => handleCopyVersion(v)}
                            className="p-1 rounded hover:bg-white/10 text-muted-foreground hover:text-white transition-colors shrink-0"
                            title={`Copy link to v${v.version_number}`}
                            data-testid={`copy-version-${v.version_number}`}
                          >
                            {copiedSlug === v.version_slug ? (
                              <Check className="w-3 h-3 text-green-400" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={handlePublish}
                    disabled={isBusy}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-md transition-colors text-sm disabled:opacity-50"
                    data-testid="update-publication"
                  >
                    {isPublishing ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RotateCw className="w-3.5 h-3.5" />
                    )}
                    New version
                  </button>
                  <button
                    onClick={handleUnpublish}
                    disabled={isBusy}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-md transition-colors text-sm disabled:opacity-50"
                    data-testid="unpublish-button"
                  >
                    {isUnpublishing ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <EyeOff className="w-3.5 h-3.5" />
                    )}
                    Unpublish
                  </button>
                </div>
              </div>
            ) : (
              /* Not published state */
              <button
                onClick={handlePublish}
                disabled={isBusy}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-md transition-colors text-sm disabled:opacity-50"
                data-testid="publish-project-button"
              >
                {isPublishing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Share2 className="w-4 h-4" />
                )}
                Publish
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
